import React, { useState } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { FileDownload, GridOn, Description } from '@mui/icons-material';
import { exportToExcel, ExportColumn } from '../../utils/exportUtils';

/**
 * Drop-in "Export" control for any list/workspace. Give it the current rows and
 * a column spec ({header, key}) and it offers Excel + CSV of exactly what's on
 * screen — so every list can export without each page reinventing it.
 */

const toCsv = (rows: Record<string, unknown>[], columns: ExportColumn[]): string => {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map((c) => esc(c.header)).join(',');
  const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
};

const ExportButton: React.FC<{
  rows: Record<string, unknown>[];
  columns: ExportColumn[];
  filename: string;
  label?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}> = ({ rows, columns, filename, label = 'Export', size = 'small', disabled }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);
  const empty = !rows.length;

  const doExcel = () => { exportToExcel(rows, columns, filename); close(); };
  const doCsv = () => {
    const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${filename}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    close();
  };

  return (
    <>
      <Button size={size} variant="outlined" startIcon={<FileDownload />} disabled={disabled || empty} onClick={(e) => setAnchor(e.currentTarget)}>
        {label}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <MenuItem onClick={doExcel}>
          <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
          <ListItemText>Excel (.xlsx)</ListItemText>
        </MenuItem>
        <MenuItem onClick={doCsv}>
          <ListItemIcon><Description fontSize="small" /></ListItemIcon>
          <ListItemText>CSV</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ExportButton;
